/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue, DropTargetBox } from './DropTargetBox';
import { DropTargetProps } from '../DropTarget/DropTargetManager';
import { BoxProps, isPropertyVisible } from '../Box/BoxExports';

const DropTargetBoxManager: ComponentManagerFn<'DropTargetBox'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'DropTargetBox'>(platform, 'DropTargetBox'),
    type: 'DropTargetBox',
    name: 'Drop Target Box',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'Box',
          'Icon',
          'IconButton',
          'IconSwitch',
          'PulseTip',
          'Text',
          'DragSourceBox',
          'TriggerOnDrop',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      ...DropTargetProps,
      ...BoxProps,
    },

    isPropertyVisible: <K extends keyof DropTargetBox>(
      property: K,
      itemData: Required<DropTargetBox>,
    ) => {
      switch (property) {
        case 'type':
        case 'onDrop':
        case 'canDropCondition':
        case 'canDrop': {
          return true;
        }
        default: {
          return isPropertyVisible(property, itemData);
        }
      }
    },
    category: 'Dashboard',
    replaceableWith: ['Box', 'DragSourceBox'],
    icon: { iconName: 'box' },
    description: `**Overview**<br/>
The Drop Target Box is a hybrid container that combines the styling flexibility of a Box with the interactive logic of a Drop Target.
In standard drag-and-drop workflows, you often need to define specific "landing zones" where items can be placed. Instead of wrapping your visual layouts inside invisible logic containers, the Drop Target Box provides a single component that handles both the visual presentation (borders, background, spacing) and the drop event logic.

**1. Drop Configuration (The Logic)**<br/>
While the component renders as a visual box, its core function is to accept incoming data from a drag event. You must configure the "handshake" to ensure only the correct items can be dropped here.
* **Type:** A mandatory string identifier (e.g., 'task-card', 'widget').
* **The Handshake:** This acts as a receiving lock. This component will only accept items from a Drag Source (or Drag Source Box) that broadcasts this exact same Type. This prevents logical errors, like dropping a "User Profile" into a "Date Field."

**2. Controlling Interaction Rules**<br/>
You often need to enforce business rules before allowing a drop (e.g., "You cannot drop a Task into the 'Done' column if it hasn't been approved").
* **Can Drop Condition:** A boolean expression (True/False). Use this for high-level state checks, such as disabling a drop zone based on user permissions or application mode.
* **canDrop Function:** A controller function for granular validation. This runs in real-time while the user is dragging an item over the box. It allows you to inspect the incoming data and return false to reject the drop dynamically.

**3. Event Handling (The Action)**<br/>
Once a valid item is dropped, you need to tell the application how to react.
* **onDrop Function:** This is the critical trigger that executes immediately after a successful drop. You typically use this function to update a Data Store, move a UI element from one list to another, or trigger a notification.

**4. Visual Layout (The Box)**<br/>
Visually, the Drop Target Box functions exactly like a standard Box component. It provides full access to CSS-based styling and Flexbox layout controls.
* **Layout & Spacing:** Control Width, Height, Padding, Margin, and Flex Direction to arrange the content inside the drop zone.
* **Styling:** Customize Background Color, Borders, and Shadows. A common pattern is to give the Drop Target Box a dashed border or a distinct background color to visually indicate to the user that it is an active drop zone.

**5. Composition & Content**<br/>
The Drop Target Box acts as a container for any content you want to display inside the drop zone (e.g., a "Drop Here" label or a list of items already dropped).
* **Allowed Children:** Visuals (Box, Icon, Text, PulseTip), Interactions (IconButton, IconSwitch), Nested Logic (DragSourceBox), and Triggers (TriggerOnDrop).

**6. Interchangeability**<br/>
* **Replaceable:** You can convert a Drop Target Box directly into a standard Box (removing drop logic) or a Drag Source Box (changing the logic from "receiving" to "giving") using the designer's replace functionality.`,
  };
};

export default DropTargetBoxManager;
